/* Makes a marquee swipeable on mobile (see the `.marquee--swipeable`
 * modifier in component-marquee.css, which turns off the CSS
 * auto-scroll animation at that breakpoint and claims horizontal touch
 * gestures for this script instead of the browser's native scrolling).
 *
 * Earlier version drove this through native overflow-x scrolling with
 * JS nudging scrollLeft every frame to auto-scroll. That was visibly
 * stuttery on a real device (and in Chrome's mobile simulator too, so
 * it wasn't an iOS-only quirk) - JS-driven scrollLeft writes force a
 * layout each time, unlike a CSS `transform` animation, which the
 * browser can run smoothly on the compositor without touching layout
 * at all. This version drives `transform` directly instead, for both
 * auto-scroll and touch-drag, so it's always exactly as smooth as the
 * desktop CSS animation - same technique, just JS-controlled here
 * instead of keyframe-controlled.
 *
 * Speed is calculated from real elapsed time between frames (not a
 * fixed 60fps assumption), so it stays consistent regardless of the
 * device's actual screen refresh rate.
 *
 * Releasing a drag carries the flick's speed and direction as momentum
 * rather than stopping dead - each frame, the current velocity blends
 * a little closer toward the steady auto-scroll velocity (exponential
 * decay), so a fast flick coasts and gradually settles into normal
 * auto-scroll instead of ever being fully stopped.
 */
(function () {
  var MOBILE_QUERY = '(max-width: 749px)';
  var VELOCITY_BLEND_MS = 400;
  var initialized = new WeakMap();

  function initSwipeableMarquee(section) {
    var track = section.querySelector('.marquee__track');
    if (!track) return;

    var halfWidth = track.scrollWidth / 2;
    var offset = 0;
    var velocity = 0;
    var isDragging = false;
    var dragStartX = 0;
    var dragStartOffset = 0;
    var lastMoveX = 0;
    var lastMoveTime = null;
    var lastTimestamp = null;
    var rafId = null;
    var duration = parseFloat(getComputedStyle(section).getPropertyValue('--marquee-duration')) || 35;

    function measure() {
      halfWidth = track.scrollWidth / 2;
    }

    function applyOffset() {
      if (halfWidth > 0) {
        offset = offset % halfWidth;
        if (offset > 0) offset -= halfWidth;
      }
      track.style.transform = 'translateX(' + offset + 'px)';
    }

    function tick(timestamp) {
      if (lastTimestamp === null) lastTimestamp = timestamp;
      var deltaMs = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      if (!isDragging && halfWidth > 0) {
        var baseVelocity = -halfWidth / (duration * 1000);
        var blend = 1 - Math.exp(-deltaMs / VELOCITY_BLEND_MS);
        velocity += (baseVelocity - velocity) * blend;
        offset += velocity * deltaMs;
        applyOffset();
      }

      rafId = requestAnimationFrame(tick);
    }

    function onTouchStart(event) {
      isDragging = true;
      dragStartX = event.touches[0].clientX;
      dragStartOffset = offset;
      lastMoveX = dragStartX;
      lastMoveTime = performance.now();
    }

    function onTouchMove(event) {
      if (!isDragging) return;
      var x = event.touches[0].clientX;
      var now = performance.now();
      offset = dragStartOffset + (x - dragStartX);
      applyOffset();

      var dt = now - lastMoveTime;
      if (dt > 0) velocity = (x - lastMoveX) / dt;
      lastMoveX = x;
      lastMoveTime = now;
    }

    function onTouchEnd() {
      isDragging = false;
      lastTimestamp = null;
    }

    section.addEventListener('touchstart', onTouchStart, { passive: true });
    section.addEventListener('touchmove', onTouchMove, { passive: true });
    section.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('resize', measure);

    rafId = requestAnimationFrame(tick);

    function teardown() {
      cancelAnimationFrame(rafId);
      section.removeEventListener('touchstart', onTouchStart);
      section.removeEventListener('touchmove', onTouchMove);
      section.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('resize', measure);
      track.style.transform = '';
    }

    initialized.set(section, teardown);
  }

  function setup() {
    var isMobile = window.matchMedia(MOBILE_QUERY).matches;
    document.querySelectorAll('[data-swipeable="true"]').forEach(function (section) {
      if (isMobile && !initialized.has(section)) {
        initSwipeableMarquee(section);
      } else if (!isMobile && initialized.has(section)) {
        initialized.get(section)();
        initialized.delete(section);
      }
    });
  }

  setup();
  window.matchMedia(MOBILE_QUERY).addEventListener('change', setup);

  window.addEventListener('pagehide', function () {
    document.querySelectorAll('[data-swipeable="true"]').forEach(function (section) {
      if (initialized.has(section)) initialized.get(section)();
    });
  });
})();
